// =============================================================================
// Wave 3.37 — ImageThumb
// -----------------------------------------------------------------------------
// A tiny clickable thumbnail that opens a lightbox dialog with the larger image
// when clicked. Renders a small placeholder square when no image is supplied.
// Used in raw-material list tables (Diamonds / Color Stones / Pearls).
// =============================================================================
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ImageOff } from 'lucide-react';

export function ImageThumb({
  src,
  alt = '',
  size = 48,
  title,
  testId,
}) {
  const [open, setOpen] = useState(false);
  const box = { width: size, height: size };
  if (!src) {
    return (
      <div
        className="rounded border border-dashed border-slate-200 bg-slate-50 text-slate-300 flex items-center justify-center"
        style={box}
        title="No image"
        data-testid={testId ? `${testId}-empty` : undefined}
      >
        <ImageOff size={Math.max(12, size / 3)} />
      </div>
    );
  }
  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="rounded border border-[var(--divider)] overflow-hidden bg-slate-50 hover:ring-2 hover:ring-[var(--gold-soft)] transition-shadow"
        style={box}
        title={title || 'View image'}
        data-testid={testId ? `${testId}-thumb` : undefined}
      >
        <img
          src={src}
          alt={alt}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl p-2" data-testid={testId ? `${testId}-lightbox` : undefined}>
          {/* Hidden but accessible title + description for screen readers */}
          <DialogTitle className="sr-only">{title || alt || 'Image preview'}</DialogTitle>
          <DialogDescription className="sr-only">
            Larger preview of the selected image.
          </DialogDescription>
          <img
            src={src}
            alt={alt}
            className="w-full max-h-[80vh] object-contain rounded"
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ImageThumb;
